import { useTranslation } from "react-i18next";
import { UserCircleIcon } from "@heroicons/react/outline";
import { SignatureConfig, SignatureField } from "../interfaces";
import SignaturesDraggableField from "./SignaturesDraggableField";
import { useState } from "react";

interface Props {
  signatureConfig: SignatureConfig;
  updateSignatureConfig: (updates: Partial<SignatureConfig>) => void;
  handleSaveAndNext: () => void;
  handleBack: () => void;
  setCurrentField: (field: SignatureField, recipient: number, offset: { left: number, top: number }) => void;
  currentField: { field: SignatureField, recipient: number } | undefined;
}

function SignaturesSidebarRegions(props: Props) {
  const { t } = useTranslation();
  const { signatureConfig } = props;

  const [isValid, setIsValid] = useState(false);

  function setCurrentField(field: SignatureField, recipient: number, offset: { left: number, top: number }) {
    setIsValid(true);
    props.setCurrentField(field, recipient, offset);
  }

  return (
    <div className="flex flex-col w-[360px] h-full bg-white flex-shrink-0 pt-6 px-4 border-l">

      <div className="text-2xl font-semibold font-gilroy mb-2">
        Insert Fields
      </div>
      <div className="text-sm font-semibold text-gray-400 mb-2">
        {t("Drag the fields required for each recipient into your document.")}
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0" />

      <div className="flex flex-col gap-4 overflow-y-scroll scrollbar-hidden py-4">
        {signatureConfig.numRecipients >= 1 &&
          <div className="flex flex-col border border-blue rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-blue border-blue border-b bg-blue/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                1st Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName1} {"<"}{signatureConfig.recipientEmail1}{">"}
              </div>
            </div>
            <div className="flex flex-col gap-2 px-2 py-2">
              <SignaturesDraggableField field="SIGNATURE" recipient={1} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="INITIALS" recipient={1} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="DATE" recipient={1} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="TEXT" recipient={1} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="CHECKBOX" recipient={1} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
            </div>
          </div>
        }

        {signatureConfig.numRecipients >= 2 &&
          <div className="flex flex-col border border-purple rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-purple border-purple border-b bg-purple/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                2nd Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName2} {"<"}{signatureConfig.recipientEmail2}{">"}
              </div>
            </div>
            <div className="flex flex-col gap-2 px-2 py-2">
              <SignaturesDraggableField field="SIGNATURE" recipient={2} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="INITIALS" recipient={2} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="DATE" recipient={2} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="TEXT" recipient={2} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="CHECKBOX" recipient={2} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
            </div>
          </div>
        }

        {signatureConfig.numRecipients >= 3 &&
          <div className="flex flex-col border border-green rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-green border-green border-b bg-green/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                3rd Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName3} {"<"}{signatureConfig.recipientEmail3}{">"}
              </div>
            </div>
            <div className="flex flex-col gap-2 px-2 py-2">
              <SignaturesDraggableField field="SIGNATURE" recipient={3} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="INITIALS" recipient={3} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="DATE" recipient={3} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="TEXT" recipient={3} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="CHECKBOX" recipient={3} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
            </div>
          </div>
        }

        {signatureConfig.numRecipients >= 4 &&
          <div className="flex flex-col border border-red rounded-xl overflow-hidden flex-shrink-0">
            <div className="text-red border-red border-b bg-red/10  px-4 py-2 flex flex-col gap-1">
              <div className="text-sm font-semibold">
                4th Recipient
              </div>
              <div className="text-xs font-semibold flex items-center gap-1 flex-shrink-0 truncate w-full">
                <UserCircleIcon className="w-4 h-4 flex-shrink-0" />
                {signatureConfig.recipientName4} {"<"}{signatureConfig.recipientEmail4}{">"}
              </div>
            </div>
            <div className="flex flex-col gap-2 px-2 py-2">
              <SignaturesDraggableField field="SIGNATURE" recipient={4} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="INITIALS" recipient={4} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="DATE" recipient={4} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="TEXT" recipient={4} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
              <SignaturesDraggableField field="CHECKBOX" recipient={4} onStartDragging={setCurrentField} currentDraggingField={props.currentField} />
            </div>
          </div>
        }
      </div>

      <div className="h-px w-full bg-gray-300 flex-shrink-0 mt-auto" />
      <div className="flex items-center text-sm gap-2 mt-4 mb-4">
        <button className="btn btn-gray border w-full px-0 py-2" onClick={props.handleBack}>Back</button>
        <button className={`btn ${isValid ? "btn-blue" : "btn-white"} w-full px-0 py-2`} onClick={props.handleSaveAndNext}>Next</button>
      </div>
    </div>
  );

}

export default SignaturesSidebarRegions;
